import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// Slice de ausencias tipificadas del empleado (F4).
// Endpoint base: /api/employeeAbsence

const initialState = {
  isLoading: false,
  error: null,
  absencesByEmployee: {}, // { [employeeId]: DtoEmployeeAbsenceGet[] }
  pending: [],            // ausencias PENDING_APPROVAL de todos los empleados
  // Sección Vacaciones (vista global del restaurante)
  list: [],               // GET /employeeAbsence/list (con employeeName, days, warnings)
  listLoading: false,
  balances: [],           // GET /employeeAbsence/vacationBalance
  balancesLoading: false,
  conflicts: [],          // GET /employeeAbsence/conflicts
  adjustments: {},        // { [`${employeeId}|${year}`]: ajuste[] }
};

const slice = createSlice({
  name: "employeeAbsences",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    stopLoading(state) { state.isLoading = false; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },

    setAbsences(state, action) {
      const { employeeId, absences } = action.payload;
      state.absencesByEmployee[employeeId] = absences || [];
      state.isLoading = false;
    },
    setPending(state, action) {
      state.pending = action.payload || [];
    },
    upsertAbsence(state, action) {
      const a = action.payload;
      const list = state.absencesByEmployee[a.employeeId] || [];
      const idx = list.findIndex((x) => x.id === a.id);
      if (idx >= 0) list[idx] = a;
      else list.unshift(a);
      state.absencesByEmployee[a.employeeId] = list;
      // Actualiza también la lista global de pending si aplica.
      const pIdx = state.pending.findIndex((x) => x.id === a.id);
      if (a.status === 1) {
        if (pIdx >= 0) state.pending[pIdx] = a;
        else state.pending.unshift(a);
      } else if (pIdx >= 0) {
        state.pending.splice(pIdx, 1);
      }
    },
    removeAbsence(state, action) {
      const { employeeId, id } = action.payload;
      const list = state.absencesByEmployee[employeeId] || [];
      state.absencesByEmployee[employeeId] = list.filter((x) => x.id !== id);
      state.pending = state.pending.filter((x) => x.id !== id);
      state.list = state.list.filter((x) => x.id !== id);
    },
    setListLoading(state, action) { state.listLoading = action.payload; },
    setList(state, action) {
      state.list = action.payload || [];
      state.listLoading = false;
    },
    // Sustituye una fila de la lista global conservando employeeName si la
    // respuesta del endpoint (approve/reject/update) no lo trae.
    patchListItem(state, action) {
      const a = action.payload;
      if (!a?.id) return;
      const idx = state.list.findIndex((x) => x.id === a.id);
      if (idx >= 0) state.list[idx] = { ...state.list[idx], ...a, employeeName: a.employeeName || state.list[idx].employeeName };
    },
    setBalancesLoading(state, action) { state.balancesLoading = action.payload; },
    setBalances(state, action) {
      state.balances = action.payload || [];
      state.balancesLoading = false;
    },
    setConflicts(state, action) { state.conflicts = action.payload || []; },
    setAdjustments(state, action) {
      const { employeeId, year, items } = action.payload;
      state.adjustments[`${employeeId}|${year}`] = items || [];
    },
  },
});

export default slice.reducer;
export const {
  setAbsences,
  setPending,
  upsertAbsence,
  removeAbsence,
  setList,
  patchListItem,
  setBalances,
  setAdjustments,
} = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────

export function getEmployeeAbsences(employeeId, year) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const q = year ? `?year=${year}` : "";
      const res = await axios.get(`/employeeAbsence/byEmployee/${employeeId}${q}`);
      dispatch(slice.actions.setAbsences({ employeeId, absences: res.data || [] }));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error cargando ausencias"));
      throw err;
    }
  };
}

export function getPendingAbsences() {
  return async (dispatch) => {
    const res = await axios.get("/employeeAbsence/pending");
    dispatch(slice.actions.setPending(res.data || []));
    return res.data;
  };
}

export function createAbsence(payload) {
  return async (dispatch) => {
    const res = await axios.post("/employeeAbsence", payload);
    dispatch(slice.actions.upsertAbsence(res.data));
    dispatch(slice.actions.patchListItem(res.data));
    return res.data;
  };
}

export function updateAbsence(payload) {
  return async (dispatch) => {
    const res = await axios.put("/employeeAbsence", payload);
    dispatch(slice.actions.upsertAbsence(res.data));
    dispatch(slice.actions.patchListItem(res.data));
    return res.data;
  };
}

export function approveAbsence(id) {
  return async (dispatch) => {
    const res = await axios.post("/employeeAbsence/approve", { id });
    dispatch(slice.actions.upsertAbsence(res.data));
    dispatch(slice.actions.patchListItem(res.data));
    return res.data;
  };
}

export function rejectAbsence(id, reason) {
  return async (dispatch) => {
    const res = await axios.post("/employeeAbsence/reject", { id, reason });
    dispatch(slice.actions.upsertAbsence(res.data));
    dispatch(slice.actions.patchListItem(res.data));
    return res.data;
  };
}

export function deleteAbsence(employeeId, id) {
  return async (dispatch) => {
    await axios.delete(`/employeeAbsence/${id}`);
    dispatch(slice.actions.removeAbsence({ employeeId, id }));
  };
}

// ── Sección Vacaciones ─────────────────────────────────────────────

const clean = (params) =>
  Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ""));

/** Lista global del restaurante. Filtros: from, to, status, type, employeeId. */
export function getAbsenceList(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.setListLoading(true));
    try {
      const res = await axios.get("/employeeAbsence/list", { params: clean(filters) });
      dispatch(slice.actions.setList(res.data || []));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.setListLoading(false));
      throw err;
    }
  };
}

/** Saldos del año (vacaciones, asuntos propios, compensatorios) por empleado. */
export function getVacationBalance(year, employeeId) {
  return async (dispatch) => {
    dispatch(slice.actions.setBalancesLoading(true));
    try {
      const res = await axios.get("/employeeAbsence/vacationBalance", { params: clean({ year, employeeId }) });
      if (!employeeId) dispatch(slice.actions.setBalances(res.data || []));
      else dispatch(slice.actions.setBalancesLoading(false));
      return res.data || [];
    } catch (err) {
      dispatch(slice.actions.setBalancesLoading(false));
      throw err;
    }
  };
}

export function getAbsenceConflicts(from, to) {
  return async (dispatch) => {
    const res = await axios.get("/employeeAbsence/conflicts", { params: clean({ from, to }) });
    dispatch(slice.actions.setConflicts(res.data || []));
    return res.data || [];
  };
}

/** Alta masiva: { employeeIds, ranges:[{start,end}], type, notes, autoApprove }. */
export function bulkCreateAbsences(payload) {
  return async () => {
    const res = await axios.post("/employeeAbsence/bulk", payload);
    return res.data || { created: [], skipped: [], warnings: [] };
  };
}

export function getVacationAdjustments(employeeId, year) {
  return async (dispatch) => {
    const res = await axios.get("/employeeAbsence/adjustments", { params: clean({ employeeId, year }) });
    dispatch(slice.actions.setAdjustments({ employeeId, year, items: res.data || [] }));
    return res.data || [];
  };
}

/** { employeeId, year, days (±), reason, kind: "VACATION" | "PERSONAL" } */
export function createVacationAdjustment(payload) {
  return async (dispatch) => {
    const res = await axios.post("/employeeAbsence/adjustments", payload);
    await dispatch(getVacationAdjustments(payload.employeeId, payload.year));
    return res.data;
  };
}

export function deleteVacationAdjustment(id, employeeId, year) {
  return async (dispatch) => {
    await axios.delete(`/employeeAbsence/adjustments/${id}`);
    await dispatch(getVacationAdjustments(employeeId, year));
  };
}
